import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  Check,
  Crown,
  Zap,
  Building2,
  Users,
  Shield,
  Brain,
  FileText,
  Loader2,
  CheckCircle,
  XCircle,
  CreditCard,
  Receipt,
  RefreshCw
} from 'lucide-react';
import api from '../services/api';
import { useToast } from '../hooks/use-toast';

const SaaSPricing = () => {
  const { toast } = useToast();
  const [searchParams] = useSearchParams();
  const [plans, setPlans] = useState([]);
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(null);
  const [statusChecking, setStatusChecking] = useState(false);

  useEffect(() => {
    fetchData();
    checkPaymentStatus();
  }, []);

  const fetchData = async () => {
    try {
      const [plansRes, subRes, transRes] = await Promise.all([
        api.get('/subscriptions/plans'),
        api.get('/subscriptions/current'),
        api.get('/subscriptions/transactions')
      ]);

      setPlans(plansRes.data.plans || []);
      setCurrentSubscription(subRes.data);
      setTransactions(transRes.data || []);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const checkPaymentStatus = async () => {
    const sessionId = searchParams.get('session_id');
    const status = searchParams.get('status');

    if (sessionId && status === 'success') {
      setStatusChecking(true);
      await pollPaymentStatus(sessionId);
    } else if (status === 'cancelled') {
      toast({
        title: 'Paiement annulé',
        description: 'Vous pouvez réessayer à tout moment',
        variant: 'destructive'
      });
    }
  };

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setStatusChecking(false);
      toast({
        title: 'Vérification en cours',
        description: 'Veuillez rafraîchir la page dans quelques instants',
        variant: 'default'
      });
      return;
    }

    try {
      const response = await api.get(`/subscriptions/checkout/status/${sessionId}`);
      const data = response.data;

      if (data.payment_status === 'paid') {
        setStatusChecking(false);
        toast({
          title: '🎉 Paiement réussi !',
          description: `Votre abonnement ${data.plan_id} est maintenant actif`,
          variant: 'default'
        });
        fetchData(); // Rafraîchir les données
        // Nettoyer l'URL
        window.history.replaceState({}, '', window.location.pathname);
        return;
      } else if (data.status === 'expired') {
        setStatusChecking(false);
        toast({
          title: 'Session expirée',
          description: 'Veuillez réessayer',
          variant: 'destructive'
        });
        return;
      }

      // Continuer le polling
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error polling status:', error);
      setStatusChecking(false);
    }
  };

  const handleSubscribe = async (planId) => {
    setCheckoutLoading(planId);

    try {
      const originUrl = window.location.origin;
      const response = await api.post('/subscriptions/checkout', {
        plan_id: planId,
        origin_url: originUrl
      });

      // Rediriger vers Stripe
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    } catch (error) {
      toast({
        title: 'Erreur',
        description: error.response?.data?.detail || 'Impossible de créer la session de paiement',
        variant: 'destructive'
      });
      setCheckoutLoading(null);
    }
  };

  const getPlanIcon = (planId) => {
    switch (planId) {
      case 'starter': return <Zap className="w-8 h-8 text-blue-500" />;
      case 'pro': return <Crown className="w-8 h-8 text-purple-500" />;
      case 'enterprise': return <Building2 className="w-8 h-8 text-amber-500" />;
      default: return <Shield className="w-8 h-8 text-gray-500" />;
    }
  };

  const getPlanGradient = (planId) => {
    switch (planId) {
      case 'starter': return 'from-blue-500 to-cyan-500';
      case 'pro': return 'from-purple-500 to-pink-500';
      case 'enterprise': return 'from-amber-500 to-orange-500';
      default: return 'from-gray-500 to-gray-600';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white p-6">
      {/* Header */}
      <div className="text-center mb-12">
        <h1 className="text-4xl font-bold text-gray-900 mb-4">
          Plans d'Abonnement
        </h1>
        <p className="text-xl text-gray-600 max-w-2xl mx-auto">
          Choisissez le plan adapté à votre société de sécurité
        </p>
      </div>

      {/* Status Checking Overlay */}
      {statusChecking && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="bg-white p-8 text-center">
            <Loader2 className="w-12 h-12 animate-spin text-blue-500 mx-auto mb-4" />
            <h3 className="text-xl font-bold mb-2">Vérification du paiement...</h3>
            <p className="text-gray-600">Veuillez patienter</p>
          </Card>
        </div>
      )}

      {/* Current Subscription */}
      {currentSubscription?.has_subscription && (
        <Card className="mb-8 bg-gradient-to-r from-green-500 to-emerald-600 text-white">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <CheckCircle className="w-10 h-10" />
                <div>
                  <h3 className="text-xl font-bold">Abonnement Actif</h3>
                  <p className="opacity-90">
                    Plan {currentSubscription.subscription?.plan_name} • 
                    {currentSubscription.plan?.max_agents === -1 ? ' Agents illimités' : ` ${currentSubscription.plan?.max_agents} agents max`}
                  </p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-3xl font-bold">{currentSubscription.plan?.price}€</p>
                <p className="opacity-90">/mois</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Plans Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto mb-12">
        {plans.map((plan) => {
          const isCurrentPlan = currentSubscription?.subscription?.plan_id === plan.id;
          
          return (
            <Card 
              key={plan.id} 
              className={`relative overflow-hidden transition-all hover:shadow-xl ${
                plan.id === 'pro' ? 'border-2 border-purple-500 scale-105' : ''
              } ${isCurrentPlan ? 'ring-2 ring-green-500' : ''}`}
            >
              {/* Popular Badge */}
              {plan.id === 'pro' && (
                <div className="absolute top-0 right-0 bg-purple-500 text-white px-4 py-1 text-sm font-bold rounded-bl-lg">
                  POPULAIRE
                </div>
              )}

              <CardHeader className={`bg-gradient-to-r ${getPlanGradient(plan.id)} text-white p-6`}>
                <div className="flex items-center gap-3">
                  {getPlanIcon(plan.id)}
                  <div>
                    <CardTitle className="text-2xl">{plan.name}</CardTitle>
                    {plan.max_agents === -1 ? (
                      <p className="text-sm opacity-90">Agents illimités</p>
                    ) : (
                      <p className="text-sm opacity-90">Jusqu'à {plan.max_agents} agents</p>
                    )}
                  </div>
                </div>
              </CardHeader>

              <CardContent className="p-6">
                {/* Price */}
                <div className="text-center mb-6">
                  <span className="text-5xl font-bold text-gray-900">{plan.price}€</span>
                  <span className="text-gray-500">/mois</span>
                </div>

                {/* Features */}
                <ul className="space-y-3 mb-6">
                  {plan.features.map((feature, i) => (
                    <li key={i} className="flex items-center gap-2">
                      <Check className="w-5 h-5 text-green-500 flex-shrink-0" />
                      <span className="text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>

                {/* CTA Button */}
                <Button
                  onClick={() => handleSubscribe(plan.id)}
                  disabled={checkoutLoading === plan.id || isCurrentPlan}
                  className={`w-full py-6 text-lg ${
                    isCurrentPlan 
                      ? 'bg-green-500 hover:bg-green-500 cursor-default'
                      : `bg-gradient-to-r ${getPlanGradient(plan.id)} hover:opacity-90`
                  }`}
                >
                  {checkoutLoading === plan.id ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Redirection...
                    </>
                  ) : isCurrentPlan ? (
                    <>
                      <CheckCircle className="w-5 h-5 mr-2" />
                      Plan actuel
                    </>
                  ) : (
                    <>
                      <CreditCard className="w-5 h-5 mr-2" />
                      S'abonner
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Transactions History */}
      {transactions.length > 0 && (
        <Card className="max-w-4xl mx-auto">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Receipt className="w-5 h-5" />
              Historique des Transactions
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {transactions.map((tx) => (
                <div 
                  key={tx.id} 
                  className="flex items-center justify-between p-4 bg-gray-50 rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    {tx.payment_status === 'paid' ? (
                      <CheckCircle className="w-5 h-5 text-green-500" />
                    ) : tx.payment_status === 'pending' ? (
                      <RefreshCw className="w-5 h-5 text-yellow-500 animate-spin" />
                    ) : (
                      <XCircle className="w-5 h-5 text-red-500" />
                    )}
                    <div>
                      <p className="font-medium">{tx.plan_name}</p>
                      <p className="text-sm text-gray-500">
                        {new Date(tx.created_at).toLocaleDateString('fr-FR')}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-bold">{tx.amount}€</p>
                    <p className={`text-sm ${
                      tx.payment_status === 'paid' ? 'text-green-600' :
                      tx.payment_status === 'pending' ? 'text-yellow-600' :
                      'text-red-600'
                    }`}>
                      {tx.payment_status === 'paid' ? 'Payé' :
                       tx.payment_status === 'pending' ? 'En attente' :
                       'Échoué'}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* FAQ */}
      <div className="max-w-3xl mx-auto mt-12">
        <h2 className="text-2xl font-bold text-center mb-8">Questions Fréquentes</h2>
        <div className="space-y-4">
          <Card>
            <CardContent className="p-4">
              <h3 className="font-bold mb-2">Puis-je changer de plan ?</h3>
              <p className="text-gray-600">Oui, vous pouvez upgrader à tout moment. Le prorata sera calculé automatiquement.</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <h3 className="font-bold mb-2">Comment annuler mon abonnement ?</h3>
              <p className="text-gray-600">Vous pouvez annuler depuis votre tableau de bord. Vous conservez l'accès jusqu'à la fin de la période payée.</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <h3 className="font-bold mb-2">Quels moyens de paiement acceptez-vous ?</h3>
              <p className="text-gray-600">Nous acceptons toutes les cartes bancaires (Visa, Mastercard, American Express) via Stripe.</p>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default SaaSPricing;
